import React, { Fragment, useEffect, useMemo, useState } from "react";
import { backend, API_URL } from "../../../config";
import {
  CssBaseline,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Box,
  Link as LinkE,
  useMediaQuery,
} from "@mui/material";

import {
  ViewCarousel as ViewCarouselIcon,
  ViewStream as ViewStreamIcon,
} from "@mui/icons-material";

import { useTheme } from "@mui/styles";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";
import "swiper/swiper.min.css";
import "swiper/components/pagination/pagination.min.css";
import "swiper/components/navigation/navigation.min.css";

import CardItem from "../../../components/Cards/Items/Items";
import ItemDialog from "../../../components/Dialogs/Items/Items";
import CategorySidebar from "../components/CategorySidebar/CategorySidebar";

import { useSelector } from "react-redux";

const Storefront = (props) => {
  SwiperCore.use([Navigation, Pagination, Autoplay]);

  const theme = useTheme();
  const xsSize = useMediaQuery(theme.breakpoints.only("xs"));
  const smSize = useMediaQuery(theme.breakpoints.only("sm"));
  const mdSize = useMediaQuery(theme.breakpoints.only("md"));
  const lgSize = useMediaQuery(theme.breakpoints.only("lg"));

  const cartStore = useSelector((state) => state.cart);
  const customerStore = useSelector((state) => state.customer);
  const currentUserStore = useSelector((state) => state.current_user);

  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [storefrontBanners, setStorefrontBanners] = useState([]);
  const [storefrontBlocks, setStorefrontBlocks] = useState([]);
  const [storefrontBlocksMapped, setStorefrontBlocksMapped] = useState(false);
  const [total, setTotal] = useState(0.0);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const getOptimalSlidesPerView = (productCount) => {
    const maxSlides = xsSize ? 1 : smSize ? 3 : mdSize ? 4 : lgSize ? 5 : 7;

    return Math.min(maxSlides, productCount);
  };

  const shouldShowNavigation = (productCount) => {
    const optimalSlides = getOptimalSlidesPerView(productCount);
    return productCount > optimalSlides;
  };

  useMemo(() => {
    if (
      products.length > 0 &&
      storefrontBlocks.length > 0 &&
      !storefrontBlocksMapped
    ) {
      const mappedBlocks = storefrontBlocks.map((block) => {
        const mappedProducts = block.products.map((upc) => {
          return products.find((product) => product.upc === upc);
        });

        return {
          ...block,
          products: mappedProducts.filter((p) => p !== undefined),
        };
      });
      setStorefrontBlocks(mappedBlocks);
      setStorefrontBlocksMapped(true);
    }
  }, [products, storefrontBlocks, storefrontBlocksMapped]);

  useEffect(() => {
    const paginate = (arr, size) => {
      return arr.reduce((acc, val, i) => {
        let idx = Math.floor(i / size);
        let page = acc[idx] || (acc[idx] = []);
        page.push(val);

        return acc;
      }, []);
    };

    const getProducts = async () => {
      let productsUPC = [];

      storefrontBlocks.forEach((block) => {
        block.products.forEach((upc) => {
          productsUPC.push(upc);
        });
      });

      const productsUPCUnique = [...new Set(productsUPC)];

      const productsUPCPag = paginate(productsUPCUnique, 100);

      let productsArray = [];

      Promise.all(
        productsUPCPag.map(async (pag) => {
          const productIdStr = pag.join("&upc=");

          await backend
            .get(
              `${API_URL}products?upc=${productIdStr}&include_inactive=true&include_nonprimary=true&no_page=1`,
            )
            .then(({ data }) => {
              productsArray = [...productsArray, ...data];
            });
        }),
      ).then(() => {
        setProducts(productsArray);
        setProductsLoaded(true);
      });
    };

    if (storefrontBlocks.length > 0 && !storefrontBlocksMapped) {
      getProducts();
    }
  }, [storefrontBlocks, storefrontBlocksMapped]);

  useEffect(() => {
    const getStorefrontBanners = async () => {
      await backend
        .get(`${API_URL}storefrontbanners?no_page=1`)
        .then(({ data }) => {
          const dataMap = data.sort(
            (a, b) => a.display_order - b.display_order,
          );

          setStorefrontBanners(dataMap);
        });
    };

    const getStorefrontBlocks = async () => {
      await backend
        .get(`${API_URL}storefrontblocks?no_page=1`)
        .then(({ data }) => {
          const dataMap = data.sort(
            (a, b) => a.display_order - b.display_order,
          );

          setStorefrontBlocks(dataMap);
          if (dataMap.length === 0) {
            setStorefrontBlocksMapped(true);
          }
        });
    };

    getStorefrontBanners();
    getStorefrontBlocks();
  }, []);

  return (
    <Fragment>
      <CssBaseline />
      <Grid container spacing={2}>
        {currentUserStore?.is_staff && (
          <Grid item xs={12} align="right">
            <Tooltip title="Storefront Banners">
              <IconButton
                onClick={() => props.history.push("/storefront/banners")}
              >
                <ViewCarouselIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Storefront Blocks">
              <IconButton
                onClick={() => props.history.push("/storefront/blocks")}
              >
                <ViewStreamIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        <Grid item xs={12} sm={3} md={3} lg={2} xl={1.5}>
          <CategorySidebar />
        </Grid>
        <Grid item xs={12} sm={9} md={9} lg={10} xl={10.5}>
          <Grid container spacing={2}>
            {storefrontBanners.length > 0 && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
                    minHeight: { xs: 200, sm: 240 },
                    bgcolor: "#efefef",
                  }}
                >
                  <Swiper
                    autoplay={{ delay: 5000 }}
                    loop={true}
                    slidesPerView={1}
                    pagination={{ clickable: true }}
                  >
                    {storefrontBanners.map((banner, index) => (
                      <SwiperSlide key={index}>
                        <Box sx={{ height: { xs: 200, sm: 240 } }}>
                          <LinkE target="_blank" href={banner.link_url}>
                            <Box
                              component="img"
                              src={banner.image_url}
                              alt={`Banner ${index + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </LinkE>
                        </Box>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Box>
              </Grid>
            )}
            {storefrontBlocks.length ? (
              storefrontBlocks.map((block, index) => (
                <Fragment key={`block-${index}`}>
                  <Grid item xs={12}>
                    <Typography variant="h6" component="div">
                      {block.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}></Grid>
                  {block.products.length ? (
                    <>
                      <Swiper
                        loop={block.products.length > 3}
                        slidesPerView={getOptimalSlidesPerView(
                          block.products.length,
                        )}
                        navigation={shouldShowNavigation(block.products.length)}
                        centeredSlides={block.products.length === 1}
                        spaceBetween={16}
                        style={{
                          width: "100%",
                        }}
                      >
                        {block.products.map((item, index) => (
                          <SwiperSlide
                            key={index}
                            style={{
                              width:
                                block.products.length < 3 ? "100%" : "auto",
                            }}
                          >
                            <Box
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <Box sx={{ width: "100%", maxWidth: 345 }}>
                                <CardItem
                                  {...props}
                                  type={1}
                                  data={item}
                                  total={total}
                                  setTotal={setTotal}
                                  setSelectedItem={setSelectedItem}
                                  setOpenItemDialog={setOpenItemDialog}
                                  reloadable={true}
                                  cartInfo={
                                    customerStore !== null
                                      ? cartStore[customerStore.id] &&
                                        cartStore[
                                          customerStore.id
                                        ].products.find(
                                          (product) => product.upc === item.upc,
                                        )
                                      : undefined
                                  }
                                  storeAddons={true}
                                  hidePricing={true}
                                />
                              </Box>
                            </Box>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  ) : (
                    <Grid item xs={12} align="center">
                      {!productsLoaded && <CircularProgress />}
                      <Typography variant="h6">
                        {!productsLoaded
                          ? "Loading products..."
                          : "No products found with the provided search criteria"}
                      </Typography>
                    </Grid>
                  )}
                </Fragment>
              ))
            ) : (
              <Grid item xs={12} align="center">
                {!storefrontBlocksMapped && <CircularProgress />}
                <Typography variant="h6">
                  {`${!storefrontBlocksMapped ? "Loading blocks information" : "There are no blocks"}`}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <ItemDialog
        data={selectedItem}
        openItemDialog={openItemDialog}
        setOpenItemDialog={setOpenItemDialog}
      />
    </Fragment>
  );
};

export default Storefront;
